import Box from './layouts/Box';
import { useMediaQuery } from '@dnhb/node_package';
import Flex from './layouts/Flex';
import WithIcon from './layouts/WithIcon';
import Card from './Card';
import ProgressBar from './ProgressBar';
import { Stack } from './layouts';
import { formatISODate } from '../../utils/dateHelpers';
import { useTheme } from '@emotion/react';
import { useNavigate } from 'react-router-dom';
import OpenSvg from 'imagesUwKluis/svg/open.svg';
import i18n from "../../i18n";
import useIconLibrary from "../../hooks/useIconLibrary";
import {FC} from "react";
import AlertMessageBanner from "./AlertMessageBanner";

export interface OverviewCardProps {
  id: number;
  title: string;
  type: OverviewCardTypes;
  createdAt: string;
  currentAmount: number;
  totalAmount: number;
  additionalInfo?: string;
  fromRequestsPage?: boolean;
  disabled?: boolean;
  completed?: boolean;
  rejectedDocuments?: number;
  sourceDataRequestType?: SourceDataRequestTypes;
  warningMessage?: string;
}

interface IconProps {
  width: string;
  height: string;
}

export enum OverviewCardTypes {
  DOCUMENT = 'document',
  SIGNING = 'signing',
  QUESTIONNAIRE = 'questionnaire',
  SOURCE_DATA = 'source_data',
  FINLY_FORM = 'finly_form',
}

export enum SourceDataRequestTypes {
  OCKTO = 'ockto',
  VEILIGOPHALEN = 'veiligophalen',
}

const paths = {
  [OverviewCardTypes.DOCUMENT]: '/requests/document-requests/',
  [OverviewCardTypes.SIGNING]: '/requests/sign-requests/',
  [OverviewCardTypes.QUESTIONNAIRE]: '/requests/questionnaires/',
  [OverviewCardTypes.SOURCE_DATA]: '/requests/source-data/',
  [OverviewCardTypes.FINLY_FORM]: '/requests/finly-forms/',
  url(type: OverviewCardTypes, id: number) {
    return this[type] + id;
  },
};

const getVariants = (iconLibrary, sourceDataRequestType?: SourceDataRequestTypes) => {
  return {
    [OverviewCardTypes.DOCUMENT]: {
      amountName: i18n.t('Geüpload'),
      Icon: iconLibrary.DocumentsIcon,
      iconNonFillable: false,
    },
    [OverviewCardTypes.SIGNING]: {
      amountName: i18n.t('Ondertekend'),
      Icon: iconLibrary.EditIcon,
      iconNonFillable: false,
    },
    [OverviewCardTypes.QUESTIONNAIRE]: {
      amountName: i18n.t('Pagina'),
      Icon: iconLibrary.QuestionnaireIcon,
      iconNonFillable: false,
    },
    [OverviewCardTypes.SOURCE_DATA]: {
      amountName: i18n.t('Gedeeld'),
      Icon: sourceDataRequestType === SourceDataRequestTypes.OCKTO ? iconLibrary.OcktoIcon : iconLibrary.VeiligOphalenIcon,
      iconNonFillable: true,
    },
    [OverviewCardTypes.FINLY_FORM]: {
      amountName: i18n.t('Afgerond'),
      Icon: iconLibrary.QuestionnaireIcon,
      iconNonFillable: false,
    },
  }
}

const OverviewCard: FC<OverviewCardProps> = ({
  id,
  title,
  fromRequestsPage = false,
  type,
  createdAt,
  additionalInfo,
  currentAmount,
  totalAmount,
  disabled,
  completed,
  rejectedDocuments,
  sourceDataRequestType,
  warningMessage
}) => {
  const navigate = useNavigate();
  const { breakpoints } = useTheme();
  const isMinTablet = useMediaQuery(
    `(min-width: ${breakpoints.md ?? '37.5rem'})`
  );
  const iconLibrary = useIconLibrary();
  const { amountName, Icon, iconNonFillable } = getVariants(iconLibrary, sourceDataRequestType)[type];

  const CustomIcon: FC<IconProps> = (props) => <Icon {...props} />;
  const isRejected = rejectedDocuments > 0;
  const cardColor = completed ? 'succes' : (isRejected || warningMessage) ? 'alert' : 'primary';

  return (
    <Card
      p={{ mobile: '20px 16px 16px 20px', desktop: '16px 24px' }}
      css={{
        position: 'relative',
        cursor: disabled ? null : 'pointer',
        overflow: 'hidden'
      }}
      onClick={disabled ? null : () => navigate(paths.url(type, id), { state: { fromRequestsPage } })}
    >
      <Flex alignItems="center" flexWrap={'nowrap'} width={'100%'}>
        {isMinTablet ? (
          <WithIcon space="m" color={iconNonFillable ? null : cardColor} css={{ flexGrow: 1 }}>
            <CustomIcon width="2rem" height="2rem" />
            <Stack space="xs" flexGrow={1}>
              <Box fontWeight="bold" lineHeight={'20px'}>
                {title}
              </Box>
              <Flex pr={'64px'}>
                <Box flex={'10% 1 1'}>{formatISODate(createdAt)}</Box>
                <Box flex={'10% 2 0'}>{additionalInfo}</Box>
                <Box flex={'10% 1 1'}>
                  {amountName}: {currentAmount}/{totalAmount}
                </Box>
              </Flex>
            </Stack>
          </WithIcon>
        ) : (
          <WithIcon space="m" color={iconNonFillable ? null : cardColor} css={{ flexGrow: 1 }}>
            <CustomIcon
              width="2rem"
              height="2rem"
              css={{ alignSelf: 'flex-start' }}
            />
            <Stack space="xs" flexGrow={1} css={{ fontSize: '14px' }}>
              <Box fontWeight="bold" lineHeight={'16px'}>
                {title}
              </Box>
              <Stack space="xs">
                <Box>{additionalInfo}</Box>
                <Flex justifyContent={'space-between'} fontSize={'10px'}>
                  <Box flex={'50%'}>{formatISODate(createdAt)}</Box>
                  <Box flex={'50%'}>
                    {amountName}: {currentAmount}/{totalAmount}
                  </Box>
                </Flex>
              </Stack>
            </Stack>
          </WithIcon>
        )}
        {!disabled && (
          <OpenSvg
            css={(theme) => ({ fill: theme.colors[cardColor], flexShrink: 0 })}
            width="1.5rem"
            height="1.5rem"
          />
        )}
      </Flex>
      {isRejected && (
        <AlertMessageBanner>
          {`${rejectedDocuments} document${rejectedDocuments > 1 ? 'en' : ''} afgekeurd`}
        </AlertMessageBanner>
      )}

      {warningMessage && <AlertMessageBanner>{warningMessage}</AlertMessageBanner>}
      <ProgressBar
        value={currentAmount}
        total={totalAmount}
        color={cardColor}
        css={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          width: '100%',
          background: 'transparent',
        }}
      />
    </Card>
  );
};

export default OverviewCard;
